/*****************************************************
Naam: DashboardMainButtons.jsx
Locatie: src/components/Dashboard/DashboardMainButtons.jsx
Datum: 2026-02-25
Versie: 2.0.0
Auteur: Ragnar Standhardt
******************************************************
Beschrijving:
Main content area met linker en rechter kolom buttons.

CHANGELOG v2.0.0 (2026-02-25):
- NIEUW: deadlineVerlopenCount prop
- NIEUW: Prijsopgave knop wordt rood bij verlopen offerte-deadlines
- NIEUW: ⏰ badge bij verlopen deadlines (prioriteit boven gewone badge)
- Badge styling gesynchroniseerd met monoliet Dashboard.jsx
******************************************************/

import React from 'react';
import { ShoppingCart, FileText, Users, Truck, MapPin, Printer, Archive, CheckCircle } from 'lucide-react';

const DashboardMainButtons = ({ 
  getButtonStyle,
  nieuweAanvragenCount,
  prijzenBinnenCount,
  opdrachtBevestigingCount,
  dotVerlopenCount,
  trackingOverdueCount,
  deadlineVerlopenCount,
  afgeschrevenCount,
  geoffreerdCount,
  factuurVervallenCount,
  onOpenAanvraag,
  onOpenPrijsopgave,
  onOpenGeoffreerd,
  onOpenOpdrachtbevestiging,
  onOpenTracking,
  klantenCount,
  vervoerdersCount,
  onOpenKlanten,
  onOpenVervoerders,
  onOpenLijsten,
  onOpenFacturen,
  onOpenAfgeschreven
}) => {
  return (
    <div className="flex-1 flex items-center justify-between px-16">
      
      {/* Left side - Small buttons */}
      <div className="flex flex-col gap-4">
        <button
          onClick={onOpenAanvraag}
          className="relative text-white p-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 w-44"
          style={getButtonStyle("aanvraagInkoop")}
        >
          {/* Badge voor nieuwe aanvragen (Prijzen nog aan te vragen) */}
          {nieuweAanvragenCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse border-2 border-white shadow-lg">
              {nieuweAanvragenCount}
            </span>
          )}
          <ShoppingCart className="w-6 h-6 mx-auto mb-2" />
          <div className="text-sm font-semibold">Nieuwe Aanvraag</div>
        </button>

        <button
          onClick={onOpenPrijsopgave}
          className="relative text-white p-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 w-44"
          style={deadlineVerlopenCount > 0 
            ? { backgroundColor: '#dc2626', color: '#ffffff', boxShadow: '0 8px 20px rgba(220,38,38,0.4), inset 0 1px 0 rgba(255,255,255,0.2)' }
            : getButtonStyle("prijsopgave")
          }
        >
          {/* Badge rechtsboven: normale count */}
          {prijzenBinnenCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse border-2 border-white shadow-lg">
              {prijzenBinnenCount}
            </span>
          )}
          {/* Badge rechtsonder: alarm bij verlopen deadlines */}
          {deadlineVerlopenCount > 0 && (
            <span className="absolute -bottom-2 -right-2 bg-red-700 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse border-2 border-white shadow-lg" title="Offerte deadline verlopen!">
              ⏰
            </span>
          )}
          <FileText className="w-6 h-6 mx-auto mb-2" />
          <div className="text-sm font-semibold">Prijsopgave Klant</div>
        </button>

        <button
          onClick={onOpenGeoffreerd}
          className="relative text-white p-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 w-44"
          style={getButtonStyle("geoffreerd")}
        >
          {geoffreerdCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse border-2 border-white shadow-lg">
              {geoffreerdCount}
            </span>
          )}
          <CheckCircle className="w-6 h-6 mx-auto mb-2" />
          <div className="text-sm font-semibold">Geoffreerd</div>
        </button>

        <button
          onClick={onOpenOpdrachtbevestiging}
          className="relative text-white p-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 w-44"
          style={getButtonStyle("prijsopgave")}
        >
          {/* Badge voor aanvragen met "Opdracht ontvangen" status (klant heeft bevestigd) */}
          {opdrachtBevestigingCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse border-2 border-white shadow-lg">
              {opdrachtBevestigingCount}
            </span>
          )}
          <FileText className="w-6 h-6 mx-auto mb-2" />
          <div className="text-sm font-semibold">Opdrachtbevestiging</div>
        </button>

        <button
          onClick={onOpenTracking}
          className="text-white p-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 w-44 relative"
          style={getButtonStyle("palletTracking")}
        >
          <MapPin className="w-6 h-6 mx-auto mb-2" />
          <div className="text-sm font-semibold">Pallet Tracking</div>
          {trackingOverdueCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-white text-red-600 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-red-600">
              {trackingOverdueCount}
            </span>
          )}
        </button>
      </div>

      {/* Right side - Small buttons */}
      <div className="flex flex-col gap-4">
        <button
          onClick={onOpenKlanten}
          className="text-white p-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 w-44 relative"
          style={getButtonStyle("klanten")}
        >
          <Users className="w-6 h-6 mx-auto mb-2" />
          <div className="text-sm font-semibold">Klanten</div>
          {klantenCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-white text-blue-800 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-blue-600 shadow">
              {klantenCount}
            </span>
          )}
        </button>


        <button
          onClick={onOpenVervoerders}
          className="text-white p-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 w-44 relative"
          style={getButtonStyle("vervoerders")}
        >
          <Truck className="w-6 h-6 mx-auto mb-2" />
          <div className="text-sm font-semibold">Vervoerders</div>
          {vervoerdersCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-white text-blue-800 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-blue-600 shadow">
              {vervoerdersCount}
            </span>
          )}
          {dotVerlopenCount > 0 && (
            <span className="absolute -bottom-2 -right-2 bg-white text-red-600 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-red-600">
              {dotVerlopenCount}
            </span>
          )}
        </button>

        <button
          onClick={onOpenLijsten}
          className="text-white p-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 w-44"
          style={getButtonStyle("lijsten")}
        >
          <Printer className="w-6 h-6 mx-auto mb-2" />
          <div className="text-sm font-semibold">Lijsten</div>
        </button>

        <button
          onClick={onOpenFacturen}
          className="relative text-white p-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 w-44"
          style={getButtonStyle("facturen")}
        >
          {factuurVervallenCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse border-2 border-white shadow-lg"
              title={`${factuurVervallenCount} factuur/facturen vervallen`}>
              {factuurVervallenCount}
            </span>
          )}
          <FileText className="w-6 h-6 mx-auto mb-2" />
          <div className="text-sm font-semibold">Facturen</div>
        </button>

        <button
          onClick={onOpenAfgeschreven}
          className="text-white p-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 w-44 relative"
          style={getButtonStyle("afgeschreven")}
        >
          {afgeschrevenCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-gray-700 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-white shadow-lg">
              {afgeschrevenCount}
            </span>
          )}
          <Archive className="w-6 h-6 mx-auto mb-2" />
          <div className="text-sm font-semibold">Afgeschreven</div>
        </button>
      </div>
    </div>
  );
};

export default DashboardMainButtons;
